import React, { useEffect, useState } from "react";
import { DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { GripVertical, Pencil, Trash2, Plus, Check, X } from "lucide-react";
import { toast } from "sonner";

function CatRow({ c, onRename, onDelete, drag, templates, onTemplate }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: c.id, disabled: !drag || !c.can_manage });
  const [edit, setEdit] = useState(null);
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }}
      className="flex items-center gap-2 px-3 py-2.5 bg-white" data-testid={`pcat-row-${c.id}`}>
      {drag && c.can_manage ? <button type="button" {...attributes} {...listeners} className="cursor-grab touch-none text-slate-400" aria-label="Drag to reorder" data-testid={`pcat-drag-${c.id}`}><GripVertical className="w-4 h-4" /></button> : <span className="w-4" />}
      {edit !== null ? (
        <form className="flex flex-1 items-center gap-1" onSubmit={(e) => { e.preventDefault(); onRename(c, edit).then((ok) => ok && setEdit(null)); }}>
          <Input value={edit} onChange={(e) => setEdit(e.target.value)} className="h-8" autoFocus data-testid={`pcat-name-${c.id}`} />
          <Button size="sm" type="submit" variant="ghost" data-testid={`pcat-save-${c.id}`}><Check className="w-4 h-4" /></Button>
          <Button size="sm" type="button" variant="ghost" onClick={() => setEdit(null)}><X className="w-4 h-4" /></Button>
        </form>
      ) : (
        <p className="flex-1 text-sm font-medium text-slate-800">{c.name}
          <span className="text-xs font-normal text-slate-400"> · {c.project_count} project{c.project_count === 1 ? "" : "s"}{c.is_personal ? " · Personal (only you)" : c.organization_name ? ` · ${c.organization_name}` : " · Account-wide"}</span></p>
      )}
      {c.can_manage && edit === null && templates && !c.is_personal && (
        <select className="h-8 max-w-[11rem] rounded-md border border-slate-200 px-1.5 text-xs text-slate-600" value={c.template_project_id || ""} title="Pathway template"
          onChange={(e) => onTemplate(c, e.target.value)} data-testid={`pcat-template-${c.id}`}>
          <option value="">No pathway template</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      )}
      {c.can_manage && edit === null && <>
        <Button size="sm" variant="ghost" onClick={() => setEdit(c.name)} data-testid={`pcat-rename-${c.id}`}><Pencil className="w-4 h-4" /></Button>
        <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => onDelete(c)} data-testid={`pcat-delete-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
      </>}
    </div>
  );
}

export function ManageCategoriesDialog({ cats, canCreate, personalOnly, onClose, onChanged }) {
  const [items, setItems] = useState(cats);
  const [name, setName] = useState("");
  const [personal, setPersonal] = useState(personalOnly || !canCreate);
  const [templates, setTemplates] = useState(null);
  useEffect(() => { if (!personalOnly) api.get("/projects/template-candidates/list").then((r) => setTemplates(r.data.items || [])).catch(() => setTemplates(null)); }, [personalOnly]);
  const setTemplate = async (c, tid) => {
    if (await call(() => api.patch(`/project-categories/${c.id}`, { template_project_id: tid || null }), tid ? "Pathway template linked" : "Pathway template removed"))
      setItems(items.map((x) => (x.id === c.id ? { ...x, template_project_id: tid || null } : x)));
  };
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const call = async (fn, msg) => { try { const r = await fn(); toast.success(msg); onChanged(); return r || true; } catch (e) { toast.error(apiError(e)); return false; } };
  const add = async (e) => {
    e.preventDefault();
    const r = await call(() => api.post("/project-categories", { name: name.trim(), personal }), "Category created");
    if (r) { setItems([...items, r.data]); setName(""); }
  };
  const rename = async (c, n) => {
    const ok = await call(() => api.patch(`/project-categories/${c.id}`, { name: n.trim() }), "Category renamed");
    if (ok) setItems(items.map((x) => (x.id === c.id ? { ...x, name: n.trim() } : x)));
    return ok;
  };
  const del = async (c) => {
    const msg = c.project_count ? `Delete "${c.name}"? Its ${c.project_count} project(s) will move to Uncategorized. No project is deleted.` : `Delete the empty category "${c.name}"?`;
    if (!window.confirm(msg)) return;
    if (await call(() => api.delete(`/project-categories/${c.id}`), "Category deleted")) setItems(items.filter((x) => x.id !== c.id));
  };
  const end = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = items.map((c) => c.id);
    const next = arrayMove(items, ids.indexOf(active.id), ids.indexOf(over.id));
    setItems(next);
    call(() => api.post("/project-categories/reorder", { ids: next.filter((c) => c.can_manage).map((c) => c.id) }), "Category order saved");
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="manage-categories-dialog">
        <DialogHeader><DialogTitle>Manage Categories</DialogTitle>
          <DialogDescription>Create your own Project Categories. Drag to set the order groups appear on Projects.</DialogDescription></DialogHeader>
        <form onSubmit={add} className="space-y-1.5"><div className="flex gap-2"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New category name" data-testid="pcat-new-name" />
          <Button type="submit" disabled={!name.trim()} className="bg-brand hover:bg-brand-strong" data-testid="pcat-add"><Plus className="w-4 h-4 mr-1" />New Category</Button></div>
          {canCreate && !personalOnly && <label className="flex items-center gap-2 text-xs text-slate-500"><input type="checkbox" checked={personal} onChange={(e) => setPersonal(e.target.checked)} data-testid="pcat-personal" />Personal category (only you, for your personal projects)</label>}</form>
        <div className="max-h-[50vh] overflow-y-auto tr-scroll">
          {items.length ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={end}>
              <SortableContext items={items.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                <div className="rounded-xl border border-slate-200 divide-y divide-slate-100">
                  {items.map((c) => <CatRow key={c.id} c={c} drag onRename={rename} onDelete={del} templates={templates} onTemplate={setTemplate} />)}
                </div>
              </SortableContext>
            </DndContext>
          ) : <p className="text-sm text-slate-400 text-center py-6" data-testid="pcat-empty">No categories yet. Projects without a category appear under Uncategorized.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
